import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface OnboardingStatus {
  isLoading: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  onboardingStep: string | null;
  clientId: string | null;
}

/**
 * Hook para controlar a navegação do onboarding
 * Redireciona o usuário para a etapa correta baseado no estado atual
 */
export function useOnboardingGuard(): OnboardingStatus {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<OnboardingStatus>({
    isLoading: true,
    isAuthenticated: false,
    isEmailVerified: false,
    onboardingStep: null,
    clientId: null,
  });

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
          setStatus({
            isLoading: false,
            isAuthenticated: false,
            isEmailVerified: false,
            onboardingStep: null,
            clientId: null,
          });
          return;
        }

        const isEmailVerified = !!session.user.email_confirmed_at;

        // Buscar dados do profile
        const { data: profile } = await supabase
          .from("profiles")
          .select("onboarding_step, client_id")
          .eq("id", session.user.id)
          .single();

        const onboardingStep = profile?.onboarding_step || "pending";
        const clientId = profile?.client_id || null;

        setStatus({
          isLoading: false,
          isAuthenticated: true,
          isEmailVerified,
          onboardingStep,
          clientId,
        });

        // Lógica de redirecionamento baseada no estado atual
        const currentPath = location.pathname;
        
        // Rotas públicas que não precisam de redirecionamento
        const publicRoutes = ["/auth", "/register", "/forgot-password", "/set-password", "/admin/login"];
        if (publicRoutes.includes(currentPath)) {
          return;
        }

        // Se não está autenticado
        if (!session.user) {
          if (!publicRoutes.includes(currentPath)) {
            navigate("/auth");
          }
          return;
        }

        // Se email não foi verificado
        if (!isEmailVerified) {
          if (currentPath !== "/verify-email") {
            navigate("/verify-email");
          }
          return;
        }

        // Rotas de onboarding
        const onboardingRoutes = [
          "/verify-email",
          "/onboarding/personal",
          "/onboarding/company",
          "/onboarding/plan",
        ];

        // Se está em uma rota de onboarding, verificar se é a correta
        if (onboardingRoutes.includes(currentPath)) {
          const expectedRoute = getExpectedRoute(onboardingStep, clientId);
          
          // Permitir voltar para etapas anteriores
          const currentIndex = onboardingRoutes.indexOf(currentPath);
          const expectedIndex = onboardingRoutes.indexOf(expectedRoute);
          
          // Só redirecionar se estiver tentando acessar uma etapa futura
          if (currentIndex > expectedIndex && onboardingStep !== "completed") {
            navigate(expectedRoute);
          }
          return;
        }

        // Se onboarding não está completo e está tentando acessar o sistema
        if (onboardingStep !== "completed" && !onboardingRoutes.includes(currentPath)) {
          const expectedRoute = getExpectedRoute(onboardingStep, clientId);
          navigate(expectedRoute);
        }

      } catch (error) {
        console.error("[useOnboardingGuard] Erro:", error);
        setStatus((prev) => ({ ...prev, isLoading: false }));
      }
    };

    checkOnboardingStatus();

    // Escutar mudanças de auth
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        checkOnboardingStatus();
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname]);

  return status;
}

/**
 * Determina a rota esperada baseado no estado do onboarding
 */
function getExpectedRoute(step: string | null, clientId: string | null): string {
  switch (step) {
    case "completed":
      return "/portal";
    case "plan":
    case "company":
      if (clientId) {
        return "/onboarding/plan";
      }
      return "/onboarding/company";
    case "personal":
      return "/onboarding/company";
    case "verified":
    case "email_sent":
    case "pending":
    default:
      return "/onboarding/personal";
  }
}

/**
 * Componente HOC para proteger rotas de onboarding
 */
export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { isLoading } = useOnboardingGuard();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return <>{children}</>;
}
